/**
 * Invite modal component for workspace header
 * 
 * This component provides a modal interface for inviting users to a workspace.
 * It includes user search, pending members list, admin toggling, and member management.
 * 
 * @module WorkspaceHeaderInviteModal
 */

import { SearchBarWithDropdown } from '@tw/tw-component-library';
import { Button, Modal } from '@/ui';
import {
  FormGroup,
  MembersList,
  MemberTag,
  MemberInfo,
  MemberName,
  MemberEmail,
  MemberActions,
  AdminToggle,
  AdminCheckbox,
  AdminLabel,
  ModalActions,
  ModalContent,
  RemoveButton,
} from '../../WorkspaceHeaderLayout.styles';
import { PendingMember, getFullName } from '../utils/workspaceHeaderUtils';

// ============= Types =============

/**
 * Props for WorkspaceHeaderInviteModal component
 */
interface WorkspaceHeaderInviteModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Array of pending members to be added */
  pendingMembers: PendingMember[];
  /** Search input value */
  memberInput: string;
  /** Array of formatted option strings for dropdown */
  options: string[];
  /** Whether search is currently loading */
  isLoading: boolean;
  /** Whether members are currently being added */
  isAddingMembers: boolean;
  /** Whether the dropdown is open */
  isOpenDropdown: boolean;
  /** Setter for dropdown open state */
  setIsOpenDropdown: (open: boolean) => void;
  /** Handler for search input changes */
  onSearch: (value: string) => void;
  /** Handler for selecting an option from dropdown */
  onSelect: (selectedLabel: string | null) => void;
  /** Handler for removing a pending member */
  onRemoveMember: (memberId: string) => void;
  /** Handler for toggling admin status */
  onToggleAdmin: (memberId: string) => void;
  /** Handler for adding all pending members */
  onAddAllMembers: () => void;
}

/**
 * Invite modal component for workspace header
 * 
 * Provides a complete interface for:
 * - Searching users by email or ID
 * - Adding users to pending list
 * - Toggling admin status for pending members
 * - Removing pending members
 * - Adding all pending members to workspace
 * 
 * @param props - Component props
 * @returns Invite modal component
 * 
 * @example
 * ```tsx
 * <WorkspaceHeaderInviteModal
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   pendingMembers={pendingMembers}
 *   memberInput={memberInput}
 *   options={options}
 *   isLoading={isLoading}
 *   isAddingMembers={isAddingMembers}
 *   isOpenDropdown={isOpen}
 *   setIsOpenDropdown={setIsOpen}
 *   onSearch={handleInviteSearch}
 *   onSelect={handleInviteSelect}
 *   onRemoveMember={removePendingMember}
 *   onToggleAdmin={togglePendingAdmin}
 *   onAddAllMembers={addAllMembers}
 * />
 * ```
 */
export const WorkspaceHeaderInviteModal: React.FC<WorkspaceHeaderInviteModalProps> = ({
  isOpen,
  onClose,
  pendingMembers,
  memberInput,
  options,
  isLoading,
  isAddingMembers,
  isOpenDropdown,
  setIsOpenDropdown,
  onSearch,
  onSelect,
  onRemoveMember,
  onToggleAdmin,
  onAddAllMembers,
}) => {
  // ============= Handlers =============

  /**
   * Handles modal close
   * 
   * Calls the onClose handler which should reset all invite state
   */
  const handleClose = () => {
    onClose();
  };

  /**
   * Gets the button text for adding members
   * 
   * @returns Formatted button text with member count
   */
  const getAddButtonText = (): string => {
    if (pendingMembers.length === 0) {
      return 'Add Members';
    }
    const count = pendingMembers.length;
    return `Add ${count} Member${count !== 1 ? 's' : ''}`;
  };

  // ============= Render =============

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Users to Workspace"
      size="medium"
      footer={
        <ModalActions>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onAddAllMembers}
            disabled={pendingMembers.length === 0}
            isLoading={isAddingMembers}
          >
            {getAddButtonText()}
          </Button>
        </ModalActions>
      }
    >
      <ModalContent>
        <FormGroup>
          <SearchBarWithDropdown
            label="Member Email or ID"
            options={options}
            placeholder="Enter member email or ID (min 3 characters)"
            onSelect={onSelect}
            onSearch={onSearch}
            searchButton={false}
            value={memberInput}
            isOpenOverride={isOpenDropdown}
            setIsOpenOverride={setIsOpenDropdown}
            isLoading={isLoading}
            getDisplayText={(option: string) => option || ''}
          />
        </FormGroup>

        {pendingMembers.length > 0 && (
          <MembersList>
            {pendingMembers.map((member) => (
              <MemberTag key={member.id}>
                <MemberInfo>
                  <MemberName>{getFullName(member)}</MemberName>
                  <MemberEmail>{member.email || member.username || ''}</MemberEmail>
                </MemberInfo>
                <MemberActions>
                  <AdminToggle>
                    <AdminCheckbox
                      type="checkbox"
                      checked={member.isOwner}
                      onChange={() => onToggleAdmin(member.id)}
                      id={`admin-${member.id}`}
                    />
                    <AdminLabel htmlFor={`admin-${member.id}`}>Owner</AdminLabel>
                  </AdminToggle>
                  <RemoveButton onClick={() => onRemoveMember(member.id)}>
                    Remove
                  </RemoveButton>
                </MemberActions>
              </MemberTag>
            ))}
          </MembersList>
        )}
      </ModalContent>
    </Modal>
  );
};
